import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { getPublicConfig } from "../../../api/config/getPublicConfig";
import { getListQuotes } from "../../../api/dossier/getListQuotes";
import { setPrimaryQuote } from "../../../api/dossier/setPrimaryQuote";
import { updateQuote } from "../../../api/quote/updateQuote";
import { APButtonLink } from "../../../components/buttons/APButtonLink";
import APContactHelpButton from "../../../components/buttons/APContactHelpButton";
import APIcon from "../../../components/buttons/APIcon";
import BackButton from "../../../components/buttons/BackButton";
import HomeButton from "../../../components/buttons/HomeButton";
import InfoButton from "../../../components/buttons/InfoButton";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../../components/form/APAutoComplete";
import APForm from "../../../components/form/APForm";
import PageContainer from "../../../components/PageContainer";
import RestrictedAppScope from "../../../components/RestrictedAppScope";
import { isAPQuoteHideInvalidCalculationsEnabled } from "../../../helpers/configuration";
import { filterQuotes, sortQuotes } from "../../../helpers/quotes";
import { useFaqs } from "../../../hooks/useFaqs";
import { useVehicleCascade } from "../../../hooks/useVehicleCascade";
import { useVehicleSourcingAccess } from "../../../hooks/useVehicleSourcingAccess";
import { useAuth } from "../../../providers/AuthProvider";
import QuoteList from "../../components/QuoteList";

type SelectionOption = "quote" | "vehicle";

type Inputs = {
  selectionOption: SelectionOption;
  selectedQuoteId: string | null;
  vehicleMake: AutoCompleteOption | null;
  vehicleModel: AutoCompleteOption | null;
  vehicleYear: AutoCompleteOption | null;
  vehicleVariant: AutoCompleteOption | null;
};

const VehicleSourcingSelectVehicle = () => {
  const { dossierId } = useParams();
  const navigate = useNavigate();
  const { inAppScope, getScopedPageUrl } = useAuth();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Inputs>({
    mode: "onTouched",
    defaultValues: { selectionOption: "quote" },
  });
  const watchSelectionOption = watch("selectionOption");
  const watchSelectedQuoteId = watch("selectedQuoteId");

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const {
    dossier,
    navigationCheckComplete,
    isLoading: isDossierLoading,
    isSuccess: isDossierSuccess,
  } = useVehicleSourcingAccess({ dossierId });

  const quotes = getListQuotes(dossierId);
  const configMap = getPublicConfig();
  const {
    makeOptions,
    modelOptions,
    yearOptions,
    variantOptions,
    isModelFieldDisabled,
    isYearFieldDisabled,
    isVariantFieldDisabled,
  } = useVehicleCascade<Inputs>({ watch, setValue });

  if (quotes.isError) {
    throw quotes.error;
  }

  const primaryQuoteId = dossier.data?.opportunity?.primaryQuoteId ?? null;
  const primaryQuote =
    quotes.data &&
    quotes.data.find(
      (quote) => quote.identifiers.crmQuoteId === primaryQuoteId
    );
  const isPrimaryQuoteLocked = primaryQuote?.config.isLocked ?? false;

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "select-vehicle-to-source",
    contextIds: {
      opportunityId: dossier?.data?.opportunity?.id,
    },
  });

  const {
    mutateAsync: mutatePrimaryToggleAsync,
    isLoading: isUpdatingPrimaryQuote,
  } = setPrimaryQuote(dossierId || "");
  const { mutateAsync: mutateAsyncUpdate, isLoading: isUpdatingQuote } =
    updateQuote(primaryQuoteId ?? "");

  const isInvalidCalculationsHidden =
    configMap.isSuccess &&
    isAPQuoteHideInvalidCalculationsEnabled(configMap.data);
  const sortedQuotes = quotes.isSuccess
    ? sortQuotes(
        filterQuotes(
          quotes.data,
          inAppScope("internal"),
          isInvalidCalculationsHidden,
          ["new"]
        ),
        "lastUpdated",
        primaryQuoteId
      )
    : [];

  const isLoading =
    isDossierLoading ||
    quotes.isLoading ||
    isUpdatingPrimaryQuote ||
    isUpdatingQuote ||
    isLoadingFaqs;
  const isSuccess = isDossierSuccess && quotes.isSuccess && isSuccessFaqs;
  const driverSourcedVehicle =
    dossier.data?.opportunity?.driverSourcedVehicle ?? false;

  const onToggleQuote = async (quoteId: string) => {
    setValue("selectedQuoteId", quoteId);
  };
  const onSubmit: SubmitHandler<Inputs> = async (_data) => {
    if (watchSelectionOption === "quote") {
      if (!!watchSelectedQuoteId && watchSelectedQuoteId !== primaryQuoteId) {
        await mutatePrimaryToggleAsync({ quoteId: watchSelectedQuoteId });
      }
    } else if (watchSelectionOption === "vehicle") {
      if (
        !!_data.vehicleVariant &&
        _data.vehicleVariant.id !== primaryQuote?.vehicle.variantId
      ) {
        await mutateAsyncUpdate({
          variantId: _data.vehicleVariant.id,
        });
      }
    }

    navigate(
      getScopedPageUrl("dossierVehicleSourcingConfirmVehicle", dossierId)
    );
  };

  useEffect(() => {
    if (watchSelectionOption === "quote") {
      setValue("vehicleMake", null);
      setValue("vehicleModel", null);
      setValue("vehicleYear", null);
      setValue("vehicleVariant", null);
    } else if (watchSelectionOption === "vehicle") {
      setValue("selectedQuoteId", null);
    }
  }, [watchSelectionOption]);

  const onToggleSelectedOption = () => {
    setValue(
      "selectionOption",
      watchSelectionOption === "quote" ? "vehicle" : "quote"
    );
  };

  const subHeaderText = driverSourcedVehicle
    ? "Choose the vehicle you want."
    : "Choose the vehicle you want to request pricing for.";

  const renderAppLocation = faqProps.renderAppLocation;

  return (
    <PageContainer
      loading={isLoading || !navigationCheckComplete}
      title="Select Vehicle"
      titleButton={
        isSuccess ? (
          <Box mr={1}>
            {renderAppLocation(
              "SelectVehicleToSource-Page",
              <InfoButton />,
              true,
              true
            )}
          </Box>
        ) : undefined
      }
    >
      {isSuccess && navigationCheckComplete && (
        <Stack spacing={2}>
          <Box>
            <Box>
              <Typography variant="labelLarge">{subHeaderText}</Typography>
            </Box>

            <APForm
              onSubmit={handleSubmit(onSubmit)}
              submitText="Next"
              submitIcon={<PlayArrowOutlined />}
              isLoading={false}
              isError={Object.keys(errors).length > 0}
              leftButton={
                <Box
                  sx={{
                    display: "flex",
                    columnGap: 1,
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{ display: "flex", columnGap: 1, alignItems: "center" }}
                  >
                    <BackButton />
                    <HomeButton destination="dossiersHome" />
                  </Box>
                </Box>
              }
              disableSubmit={
                watchSelectionOption === "quote" && !watchSelectedQuoteId
              }
            >
              {watchSelectionOption === "quote" && (
                <Stack spacing={2}>
                  <QuoteList
                    quotes={sortedQuotes}
                    primaryQuoteId={primaryQuoteId}
                    actions={{
                      actionHandlers: {
                        onToggleCompare: onToggleQuote,
                      },
                      topActions: ["onToggleCompare", "onSetPrimary"],
                      onCardClick: onToggleQuote,
                    }}
                    config={{
                      disableCardLink: true,
                      hidePrimaryToggle: true,
                      hidePricing: true,
                      compareTextOverride: "Select",
                      quotesInComparison: watchSelectedQuoteId
                        ? [watchSelectedQuoteId]
                        : [],
                      highlightCompare: true,
                    }}
                  />
                  {renderAppLocation(
                    "SelectVehicleToSource-NewQuotesOnlyNote",
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "start",
                        columnGap: 1,
                      }}
                    >
                      <APIcon name="warning" color="warning" size="small" />
                      <Typography variant="labelLarge">
                        Only quotes on new vehicles displayed.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}

              {watchSelectionOption === "vehicle" && (
                <>
                  <APAutoComplete
                    name="vehicleMake"
                    label="Vehicle Make"
                    options={makeOptions}
                    control={control}
                    validations={{ required: true }}
                    errors={errors}
                    defaultValue={null}
                  />

                  <APAutoComplete
                    name="vehicleModel"
                    label="Vehicle Model"
                    options={modelOptions}
                    control={control}
                    validations={{ required: true }}
                    errors={errors}
                    defaultValue={null}
                    disabled={isModelFieldDisabled}
                  />

                  <APAutoComplete
                    name="vehicleYear"
                    label="Vehicle Year"
                    options={yearOptions}
                    control={control}
                    validations={{ required: true }}
                    errors={errors}
                    defaultValue={null}
                    disabled={isYearFieldDisabled}
                  />

                  <APAutoComplete
                    name="vehicleVariant"
                    label="Vehicle Variant"
                    options={variantOptions}
                    control={control}
                    validations={{ required: true }}
                    errors={errors}
                    defaultValue={null}
                    disabled={isVariantFieldDisabled}
                  />
                </>
              )}

              {!isPrimaryQuoteLocked && (
                <Box>
                  <APButtonLink
                    text={
                      watchSelectionOption === "quote"
                        ? "Choose a vehicle"
                        : "Select a Quote I have built"
                    }
                    onClick={onToggleSelectedOption}
                    noUnderline
                  />
                </Box>
              )}
            </APForm>
            <Box>
              <RestrictedAppScope scope="external">
                <APContactHelpButton
                  regardingOpportunityId={
                    dossier.data?.opportunity?.id ?? undefined
                  }
                />
              </RestrictedAppScope>
            </Box>
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
};

export default VehicleSourcingSelectVehicle;
